import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import { Box, makeStyles } from '@material-ui/core';

import Page from '~/components/shared/Page';
import Header from '~/components/layouts/DashboardLayout/components/Header';
import { getExplorer3dById } from '~/services/api';
import { ArrowLeft } from '~/views/private/billings/components/icons/ArrowLeft';
import { Cube } from '~/views/private/overview/Components/icons/Cube';
import { LeadIcon2 } from '~/views/private/overview/Components/icons/LeadIcon2';
import { ContentEditor } from '~/views/private/overview/ContentEditor/ContentEditor';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    padding: 'var(--utilities-size-1-500) var(--utilities-size-1-500) 0',
    boxSizing: 'border-box',
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    padding: '0 0 var(--utilities-size-0-750) 0',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-000)',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default)',
  },
  headerTextSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-000)',
    flex: '1 0 0',
  },
  headerButtonsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
  },
  backButton: {
    display: 'flex',
    width: '52px',
    height: '52px',
    minHeight: 'var(--utilities-size-2-500)',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
    background: 'var(--surface-tint-weak)',
    boxShadow: 'var(--shadows-subtle)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-tint-weak-hover)',
    },
  },
  backButtonIcon: {
    width: 'var(--utilities-size-1-250)',
    height: 'var(--utilities-size-1-250)',
  },
  headerTitleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: '1 0 0',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
  },
  headerTitleText: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-xl)',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 'var(--typography-line-height-xl-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  headerTitleCurrent: {
    color: 'var(--content-primary)',
  },
  headerSubTitleText: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  leadIcon2: {
    height: 'var(--utilities-size-1-000)',
    width: 'var(--utilities-size-1-000)',
  },
  explorerButton: {
    display: 'flex',
    minHeight: 'var(--utilities-size-2-500)',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    borderRadius: 'var(--utilities-border-radius-default)',
    background: 'var(--surface-accent)',
    boxShadow: 'var(--shadows-subtle)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-accent-hover)',
    },
  },
  buttonIcon: {
    width: 'var(--utilities-size-1-250)',
    height: 'var(--utilities-size-1-250)',
  },
  explorerButtonText: {
    color: 'var(--content-inverted)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 'var(--typography-line-height-md-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  editor: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    margin: '0 calc(var(--utilities-size-1-500) * -1)',
  },
}));

const Explorer3DContentEditorView = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const companyStats = useSelector(state => state.companyStats.data);
  const userEmail = useSelector(state => {
    const user = state.auth && state.auth.user;
    return (user && (user.email || user.emailAddress)) || '';
  });

  let { listId, companyName, explorer3DName } = useParams();
  listId = decodeURIComponent(listId);
  companyName = decodeURIComponent(companyName);
  explorer3DName = decodeURIComponent(explorer3DName);

  const summaryPath = `/overview/multi/3dExplorer/${encodeURIComponent(
    companyName,
  )}/${encodeURIComponent(listId)}/${encodeURIComponent(explorer3DName)}`;
  const propertyPath = `/overview/multi/${encodeURIComponent(
    companyName,
  )}/${encodeURIComponent(listId)}`;

  const explorer = useAsync(async () => {
    const propertyData = (companyStats || []).find(
      company =>
        company.companyName === companyName &&
        decodeURIComponent(company.property_name) === listId,
    );
    const explorerMatch =
      propertyData &&
      propertyData.explorers3D &&
      propertyData.explorers3D.find(item => item.name === explorer3DName);
    const locationId = explorerMatch && explorerMatch.locationId;

    if (!locationId) {
      throw new Error('Explorer locationId not found');
    }

    const data = await getExplorer3dById(locationId);
    return {
      locationId,
      id: data && data.id,
    };
  }, [companyStats, companyName, listId, explorer3DName]);

  return (
    <Page className={classes.root}>
      <Header>
        <Box className={classes.headerContainer}>
          <Box className={classes.headerTextSection}>
            <Box
              className={classes.backButton}
              onClick={() => navigate(summaryPath)}
            >
              <ArrowLeft className={classes.backButtonIcon} />
            </Box>
            <Box className={classes.headerTitleContainer}>
              <Box className={classes.headerTitle}>
                <Box className={classes.headerTitleText}>
                  <Link to={propertyPath}>{listId}</Link>
                </Box>
                <LeadIcon2 className={classes.leadIcon2} />
                <Box className={classes.headerTitleText}>
                  <Link to={summaryPath}>{explorer3DName}</Link>
                </Box>
                <LeadIcon2 className={classes.leadIcon2} />
                <Box
                  className={`${classes.headerTitleText} ${classes.headerTitleCurrent}`}
                >
                  Content Editor
                </Box>
              </Box>
              <Box className={classes.headerSubTitleText}>
                3D Explorer Summary
              </Box>
            </Box>
          </Box>
          <Box className={classes.headerButtonsSection}>
            <a
              href={`${process.env.EXPLORER3D_URL}/overview?explorer3dId=${
                explorer.value && explorer.value.locationId
              }`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              <Box className={classes.explorerButton}>
                <Cube
                  className={classes.buttonIcon}
                  color="var(--content-inverted)"
                />
                <Box className={classes.explorerButtonText}>3D Explorer</Box>
              </Box>
            </a>
          </Box>
        </Box>
      </Header>

      <Box className={classes.editor}>
        <ContentEditor
          explorer3dId={explorer.value && explorer.value.id}
          updatedBy={userEmail}
        />
      </Box>
    </Page>
  );
};

export default Explorer3DContentEditorView;
